import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CreditCard, Landmark, Banknote, Trash2, ArrowRight, ArrowLeft, ShoppingCart, CheckCircle2 } from "lucide-react";
import { MediaTypeBadges } from "@/components/Badges";

const METHODS = [
  { key: "card", label: "Pay by card", desc: "Instant access via Stripe", icon: CreditCard },
  { key: "bank_transfer", label: "Bank transfer", desc: "Order held until payment confirmed", icon: Landmark },
  { key: "cash", label: "Cash / Other", desc: "Order held until payment confirmed", icon: Banknote },
];

export default function Checkout() {
  const { items, remove, update, clear, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [billing, setBilling] = useState({ line1: "", city: "", postcode: "", country: "New Zealand" });
  const [method, setMethod] = useState("card");
  const [code, setCode] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [brandLogo, setBrandLogo] = useState("");

  useEffect(() => { api.get("/public-settings").then(({ data }) => setBrandLogo(data.brand_logo || "")).catch(() => {}); }, []);

  useEffect(() => { if (user) { setEmail(user.email || ""); setName(user.name || ""); } }, [user]);

  const cartPayload = useMemo(() => items.map((i) => ({
    media_item_id: i.media_item_id,
    performance_name: i.performance_name || "",
    performance_studio: i.performance_studio || "",
    performance_number: i.performance_number || "",
  })), [items]);

  useEffect(() => {
    if (step !== 2 || items.length === 0) return;
    api.post("/checkout/quote", { payment_method: method, items: cartPayload, discount_code: discountCode || null })
      .then(({ data }) => setQuote(data))
      .catch((err) => {
        if (discountCode) { toast.error(formatApiError(err.response?.data?.detail)); setDiscountCode(""); }
        else setQuote(null);
      });
  }, [step, method, cartPayload, items.length, discountCode]);

  useEffect(() => {
    if (items.length === 0 || !email.includes("@")) return;
    const t = setTimeout(() => {
      api.post("/cart/track", { email, items: cartPayload, origin_url: window.location.origin }).catch(() => {});
    }, 1000);
    return () => clearTimeout(t);
  }, [email, cartPayload, items.length]);

  const needsPerf = (i) => i.category === "dance_competition" || i.category === "event";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        customer_email: email, name, payment_method: method,
        items: cartPayload, billing, discount_code: discountCode || null, origin_url: window.location.origin,
      });
      if (data.checkout_url) { clear(); window.location.href = data.checkout_url; return; }
      clear();
      setPlaced(data);
      toast.success("Order placed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (placed) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]"><Navbar />
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          <div data-testid="order-placed" className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/70 p-8">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
            <h2 className="font-display text-xl font-bold text-white">Order placed</h2>
            <p className="font-mono-x text-sm text-zinc-400">{placed.order_number}</p>
            <p className="text-sm text-zinc-400">{placed.message}</p>
            <Button onClick={() => navigate("/login")} className="w-full bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Sign in to track your order</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]"><Navbar />
        <div className="mx-auto max-w-md px-4 py-24 text-center">
          <ShoppingCart className="mx-auto mb-4 h-10 w-10 text-zinc-600" />
          <h1 className="font-display text-2xl font-bold text-white">Your cart is empty</h1>
          <p className="mt-2 text-sm text-zinc-500">Browse events and add media to get started.</p>
          <Button data-testid="empty-browse" onClick={() => navigate("/")} className="mt-6 bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Browse events</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100"><Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <StepDot n={1} label="Your details" active={step === 1} done={step > 1} />
          <div className="h-px flex-1 bg-zinc-800" />
          <StepDot n={2} label="Billing & payment" active={step === 2} done={false} />
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {step === 1 ? (
              <div className="space-y-6 rounded-xl border border-zinc-800 bg-zinc-900/70 p-6">
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Your details</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label className="text-zinc-300">Name</Label><Input data-testid="co-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                  <div><Label className="text-zinc-300">Email</Label><Input data-testid="co-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                </div>
                {items.some(needsPerf) && (
                  <div className="space-y-4">
                    <h3 className="font-display text-sm font-bold uppercase tracking-tight text-zinc-400">Order Details</h3>
                    {items.filter(needsPerf).map((i) => <PerfBlock key={i.media_item_id} item={i} update={update} />)}
                  </div>
                )}
                <Button data-testid="co-to-step2" onClick={() => { if (!email.includes("@")) return toast.error("Enter a valid email"); setStep(2); }} className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
                  Continue to payment <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-6 rounded-xl border border-zinc-800 bg-zinc-900/70 p-6">
                <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to details</button>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Billing address</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Label className="text-zinc-300">Address</Label><Input data-testid="co-line1" value={billing.line1} onChange={(e) => setBilling({ ...billing, line1: e.target.value })} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                  <div><Label className="text-zinc-300">City</Label><Input data-testid="co-city" value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                  <div><Label className="text-zinc-300">Postcode</Label><Input data-testid="co-postcode" value={billing.postcode} onChange={(e) => setBilling({ ...billing, postcode: e.target.value })} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                  <div className="sm:col-span-2"><Label className="text-zinc-300">Country</Label><Input data-testid="co-country" value={billing.country} onChange={(e) => setBilling({ ...billing, country: e.target.value })} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Payment method</Label>
                  {METHODS.map((m) => { const Icon = m.icon; return (
                    <button type="button" key={m.key} data-testid={`method-${m.key}`} onClick={() => setMethod(m.key)}
                      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${method === m.key ? "border-[#ff2a5f] bg-[#ff2a5f]/10" : "border-zinc-700 hover:border-zinc-500"}`}>
                      <Icon className={`h-5 w-5 ${method === m.key ? "text-[#ff2a5f]" : "text-zinc-400"}`} />
                      <span><span className="block text-sm font-semibold text-white">{m.label}</span><span className="block text-xs text-zinc-500">{m.desc}</span></span>
                    </button>
                  ); })}
                </div>
                <Button data-testid="place-order" onClick={submit} disabled={busy} className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : method === "card" ? `Pay ${quote ? money(quote.total) : ""}` : "Place order"}
                </Button>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="space-y-4">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-5">
              {brandLogo && <img data-testid="checkout-brand-logo" src={brandLogo} alt="Brand" className="mb-3 h-8 w-auto object-contain" />}
              <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-tight text-white">Order summary</h3>
              <div className="space-y-3">
                {items.map((i) => {
                  const qi = quote?.items?.find((x) => x.media_item_id === i.media_item_id);
                  const price = qi ? qi.price : i.price;
                  return (
                  <div key={i.media_item_id} data-testid={`cart-item-${i.media_item_id}`} className="flex items-center gap-3">
                    <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-zinc-950">{i.thumbnail_url && <img src={i.thumbnail_url} alt={i.title} className="h-full w-full object-cover" />}</div>
                    <div className="min-w-0 flex-1">
                      {i.event_name && <div className="truncate text-sm font-semibold text-white" data-testid={`cart-product-${i.media_item_id}`}>{i.event_name}</div>}
                      <div className="truncate text-xs text-zinc-400">{i.title}</div>
                      <div className="mt-1 flex items-center gap-2">
                        {(i.content_type || i.delivery_download || i.delivery_usb) && <MediaTypeBadges types={[...(i.content_type ? [i.content_type] : []), ...(i.delivery_download ? ["download"] : []), ...(i.delivery_usb ? ["usb"] : [])]} testidPrefix={`cart-badge-${i.media_item_id}`} />}
                        <span className="text-xs text-zinc-500">{qi?.package && price === 0 ? "Included in package" : money(price)}</span>
                      </div>
                    </div>
                    <button data-testid={`remove-cart-${i.media_item_id}`} onClick={() => remove(i.media_item_id)} className="text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  );
                })}
              </div>
              {step === 2 && (
                <div className="mt-4 border-t border-zinc-800 pt-4">
                  <Label className="text-zinc-300">Discount code</Label>
                  <div className="mt-1 flex gap-2">
                    <Input data-testid="promo-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" className="h-9 border-zinc-700 bg-zinc-950 text-sm text-white" onKeyDown={(e) => e.key === "Enter" && setDiscountCode(code.trim().toUpperCase())} />
                    <Button data-testid="apply-promo" type="button" onClick={() => setDiscountCode(code.trim().toUpperCase())} className="h-9 bg-zinc-800 text-white hover:bg-zinc-700">Apply</Button>
                  </div>
                  {quote && quote.discount > 0 && (
                    <div data-testid="promo-applied" className="mt-2 flex items-center justify-between rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
                      <span>{quote.discount_label} applied</span>
                      <button data-testid="remove-promo" onClick={() => { setDiscountCode(""); setCode(""); }} className="text-emerald-400 hover:text-white">Remove</button>
                    </div>
                  )}
                </div>
              )}
              <div className="mt-4 space-y-1.5 border-t border-zinc-800 pt-4 text-sm">
                <Row label="Subtotal" value={money(quote ? quote.subtotal : subtotal)} />
                {step === 2 && quote && quote.discount > 0 && <Row label={quote.discount_label || "Discount"} value={`- ${money(quote.discount)}`} testid="discount-line" />}
                {step === 2 && quote && quote.surcharge > 0 && <Row label={`Card surcharge (${(quote.surcharge_rate * 100).toFixed(1)}%)`} value={money(quote.surcharge)} testid="surcharge-line" />}
                <div className="flex items-center justify-between pt-2 font-display text-lg font-extrabold text-white">
                  <span>Total (incl. GST)</span><span data-testid="cart-total">{money(quote ? quote.total : subtotal)}</span>
                </div>
                {step === 2 && quote && <div data-testid="tax-line" className="text-right text-xs text-zinc-500">Includes {money(quote.gst)} GST</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Row({ label, value, muted, testid }) {
  return <div className={`flex items-center justify-between ${muted ? "text-zinc-500" : "text-zinc-300"}`} data-testid={testid}><span>{label}</span><span>{value}</span></div>;
}

function StepDot({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${active || done ? "bg-[#ff2a5f] text-white" : "bg-zinc-800 text-zinc-500"}`}>{n}</div>
      <span className={`text-sm ${active ? "text-white" : "text-zinc-500"}`}>{label}</span>
    </div>
  );
}

function PerfBlock({ item, update }) {
  const f = item.fields || {};
  const preset = item.performance_mode === "preset" && (item.performances || []).length > 0;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <div className="mb-2 text-sm font-medium text-white">{item.title}</div>
      {preset ? (
        <Select onValueChange={(v) => { const p = item.performances.find((x) => x.id === v); if (p) update(item.media_item_id, { performance_name: p.name, performance_studio: p.studio, performance_number: p.number }); }}>
          <SelectTrigger data-testid={`perf-select-${item.media_item_id}`} className="border-zinc-700 bg-zinc-900 text-white"><SelectValue placeholder="Select a performance" /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
            {item.performances.map((p) => <SelectItem key={p.id} value={p.id}>{[p.name, p.studio, p.number].filter(Boolean).join(" — ")}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <div className="grid gap-2 sm:grid-cols-3">
          {(!f.name || f.name.enabled !== false) && <Input data-testid={`perf-name-${item.media_item_id}`} value={item.performance_name || ""} onChange={(e) => update(item.media_item_id, { performance_name: e.target.value })} placeholder={f.name?.label || "Dancer / team name"} className="h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />}
          {(!f.studio || f.studio.enabled !== false) && <Input data-testid={`perf-studio-${item.media_item_id}`} value={item.performance_studio || ""} onChange={(e) => update(item.media_item_id, { performance_studio: e.target.value })} placeholder={f.studio?.label || "Studio"} className="h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />}
          {(!f.number || f.number.enabled !== false) && <Input data-testid={`perf-number-${item.media_item_id}`} value={item.performance_number || ""} onChange={(e) => update(item.media_item_id, { performance_number: e.target.value })} placeholder={f.number?.label || "Competitor number"} className="h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />}
        </div>
      )}
    </div>
  );
}
